import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import './App.css';

// Components
import Login from './components/Login';
import TeacherDashboard from './components/TeacherDashboard';
import StudentDashboard from './components/StudentDashboard';
import CreateQuiz from './components/CreateQuiz';
import EditQuiz from './components/EditQuiz';
import SessionLobby from './components/SessionLobby';
import StudentJoin from './components/StudentJoin';
import GamePlay from './components/GamePlay';
import TeacherGameControl from './components/TeacherGameControl';
import QuizLeaderboard from './components/QuizLeaderboard';
import ClassroomCallback from './components/ClassroomCallback';
import IndividualQuiz from './components/IndividualQuiz';
import IndividualResults from './components/IndividualResults';
import CreateHangman from './components/CreateHangman';
import HangmanGame from './components/HangmanGame';
import HangmanRankings from './components/HangmanRankings';
import CreateLinuxQuest from './components/CreateLinuxQuest';
import QuestProgress from './components/QuestProgress';
import TerminalGame from './components/TerminalGame';
import TeacherRequest from './components/TeacherRequest';
import AdminDashboard from './components/AdminDashboard';
import CreateTextHunt from './components/CreateTextHunt';
import TextHuntGame from './components/TextHuntGame';
import EditTextHunt from './components/EditTextHunt';
import TextHuntResult from './components/TextHuntResult';
import TextHuntResults from './components/TextHuntResults';
import ClassLeaderboard from './components/ClassLeaderboard';
import CreateOdooActivity from './components/CreateOdooActivity';
import EditOdooActivity from './components/EditOdooActivity';
import OdooActivityGame from './components/OdooActivityGame';
import TeacherOdooMonitor from './components/TeacherOdooMonitor';
import OdooActivityMonitor from './components/OdooActivityMonitor';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in (from localStorage)
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('user');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary to-purple-900">
        <div className="text-white text-2xl">Cargando...</div>
      </div>
    );
  }

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <Router>
        <div className="App min-h-screen bg-gray-50">
          <Routes>
            <Route path="/" element={!user ? <Login onLogin={handleLogin} /> : <Navigate to="/dashboard" />} />
            <Route 
              path="/dashboard" 
              element={user ? (
                user.role === 'admin' ? (
                  <Navigate to="/admin" />
                ) : user.role === 'teacher' ? (
                  <TeacherDashboard user={user} onLogout={handleLogout} />
                ) : (
                  <StudentDashboard user={user} onLogout={handleLogout} />
                )
              ) : <Navigate to="/" />} 
            />
            <Route 
              path="/quiz/create" 
              element={user && user.role === 'teacher' ? <CreateQuiz user={user} /> : <Navigate to="/" />} 
            />
            <Route 
              path="/quiz/edit/:quizId" 
              element={user && user.role === 'teacher' ? <EditQuiz user={user} /> : <Navigate to="/" />} 
            />
            <Route 
              path="/session/:pin/lobby" 
              element={user && user.role === 'teacher' ? <SessionLobby user={user} /> : <Navigate to="/" />} 
            />
            <Route 
              path="/session/:pin/control" 
              element={user && user.role === 'teacher' ? <TeacherGameControl user={user} /> : <Navigate to="/" />} 
            />
            <Route 
              path="/join" 
              element={user ? <StudentJoin user={user} /> : <Navigate to="/" />} 
            />
            <Route 
              path="/play/:pin" 
              element={user ? <GamePlay user={user} /> : <Navigate to="/" />} 
            />
            <Route 
              path="/quiz/:quizId/individual" 
              element={user ? <IndividualQuiz user={user} /> : <Navigate to="/" />} 
            />
            <Route 
              path="/teacher/individual-results" 
              element={user && user.role === 'teacher' ? <IndividualResults user={user} /> : <Navigate to="/" />} 
            />
            <Route 
              path="/teacher/leaderboard" 
              element={user && user.role === 'teacher' ? <ClassLeaderboard user={user} /> : <Navigate to="/" />} 
            />
            <Route 
              path="/hangman/create" 
              element={user && user.role === 'teacher' ? <CreateHangman user={user} /> : <Navigate to="/" />} 
            />
            <Route 
              path="/hangman/edit/:hangmanId" 
              element={user && user.role === 'teacher' ? <CreateHangman user={user} isEditMode={true} /> : <Navigate to="/" />} 
            />
            <Route 
              path="/hangman/:hangmanId/play" 
              element={user ? <HangmanGame user={user} /> : <Navigate to="/" />} 
            />
            <Route 
              path="/hangman/:hangmanId/rankings" 
              element={user ? <HangmanRankings user={user} /> : <Navigate to="/" />}
            />
            <Route 
              path="/terminal-quest/create" 
              element={user && user.role === 'teacher' ? <CreateLinuxQuest user={user} /> : <Navigate to="/" />} 
            />
            <Route 
              path="/terminal-quest/edit/:questId" 
              element={user && user.role === 'teacher' ? <CreateLinuxQuest user={user} /> : <Navigate to="/" />} 
            />
            <Route 
              path="/terminal-quest/:questId/play" 
              element={user ? <TerminalGame user={user} /> : <Navigate to="/" />} 
            />
            <Route 
              path="/terminal-quest/:questId/progress" 
              element={user && user.role === 'teacher' ? <QuestProgress /> : <Navigate to="/" />} 
            />
            <Route 
              path="/texthunt/create" 
              element={user && user.role === 'teacher' ? <CreateTextHunt user={user} /> : <Navigate to="/" />} 
            />
            <Route 
              path="/texthunt/edit/:texthuntId" 
              element={user && user.role === 'teacher' ? <EditTextHunt user={user} /> : <Navigate to="/" />} 
            />
            <Route 
              path="/texthunt/:texthuntId/play" 
              element={user ? <TextHuntGame user={user} /> : <Navigate to="/" />} 
            />
            <Route 
              path="/texthunt/:texthuntId/result/:studentId" 
              element={user ? <TextHuntResult user={user} /> : <Navigate to="/" />} 
            />
            <Route 
              path="/texthunt/:texthuntId/results" 
              element={user && user.role === 'teacher' ? <TextHuntResults user={user} /> : <Navigate to="/" />} 
            />
            <Route 
              path="/quiz/:quizId/leaderboard" 
              element={<QuizLeaderboard />} 
            />
            <Route 
              path="/classroom/callback" 
              element={<ClassroomCallback />} 
            />
            <Route 
              path="/teacher-request" 
              element={
                <TeacherRequest 
                  user={user || JSON.parse(localStorage.getItem('pendingTeacherRequest') || '{}')} 
                />
              } 
            />
            <Route 
              path="/admin" 
              element={user && user.role === 'admin' ? <AdminDashboard user={user} /> : <Navigate to="/" />} 
            />
            <Route 
              path="/teacher" 
              element={user && (user.role === 'teacher' || user.role === 'admin') ? <TeacherDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/" />} 
            />
            <Route 
              path="/odoo-activity/create" 
              element={user && user.role === 'teacher' ? <CreateOdooActivity user={user} /> : <Navigate to="/" />} 
            />
            <Route 
              path="/odoo-activity/edit/:id" 
              element={user && user.role === 'teacher' ? <EditOdooActivity user={user} /> : <Navigate to="/" />} 
            />
            <Route 
              path="/odoo-activity/:activityId/play" 
              element={user ? <OdooActivityGame user={user} /> : <Navigate to="/" />} 
            />
            <Route 
              path="/odoo-activity/:activityId/monitor" 
              element={user && user.role === 'teacher' ? <OdooActivityMonitor user={user} /> : <Navigate to="/" />} 
            />
          </Routes>
        </div>
      </Router>
    </GoogleOAuthProvider>
  );
}

export default App;
